<script setup lang="ts">
import { CategoryType, type SearchSuggestions } from "mws-ts-v2-sdk";

import { simplifiedString } from "~/utils/stringUtils";

const { searchTerm, searchSuggestions } = defineProps<{
  searchSuggestions: SearchSuggestions[];
  searchTerm: string;
  isMobile?: boolean;
}>();

type Emits = {
  (e: "enter-search-result", input: string): void;
};

const emit = defineEmits<Emits>();
const localePath = useLocalePath();

const { searchSuggestionClicked, searchItemClicked } = useAnalytics();

const searchSuggestionsShown = computed(() => searchSuggestions.length > 0);

const { closeSearchModal } = useSearchBar();
const { addSearchHistory } = useSearchHistory();

const onSearchSuggestionClicked = (suggestion: SearchSuggestions) => {
  searchSuggestionClicked({
    suggestion_category_id: suggestion.id,
    search_suggestion: suggestion.name,
    search_term: searchTerm,
  });

  addSearchHistory(suggestion);

  // close the modal on next tick to allow the search history state to update
  nextTick().then(() => {
    closeSearchModal();
  });
};

const onSearchAllClicked = (searchTerm: string) => {
  searchItemClicked({
    type: "seeAll",
    tab_origin: "quickSearch",
    search_term: searchTerm,
  });
  emit("enter-search-result", searchTerm);
};

const highlightSuggestion = (suggestion: string) => {
  const startOfStringMatch = simplifiedString(suggestion).indexOf(
    simplifiedString(searchTerm),
  );

  if (startOfStringMatch > -1 && searchTerm) {
    return [
      {
        value: suggestion.substring(0, startOfStringMatch),
        type: "template",
      },
      {
        value: suggestion.substring(
          startOfStringMatch,
          startOfStringMatch + searchTerm.length,
        ),
        type: "bold",
      },
      {
        value: suggestion.substring(startOfStringMatch + searchTerm.length),
        type: "template",
      },
    ].filter((split) => split.value);
  }

  return [
    {
      value: suggestion,
      type: "template",
    },
  ];
};

const { result } = useGrowthbook({
  feature: "search-category-navigation-web",
  fallback: false,
});

const getSuggestionLink = (suggestion: SearchSuggestions) => {
  if (
    result.value &&
    (suggestion.type === CategoryType.Team ||
      suggestion.type === CategoryType.Campaign)
  ) {
    return localePath({
      path: `/category/${suggestion.slug}`,
    });
  } else {
    return localePath({ path: `/search/${suggestion.name}` });
  }
};
</script>

<template>
  <div>
    <h2
      v-if="searchSuggestionsShown"
      data-test="suggestions-header"
      class="font-semibold uppercase text-base mb-3"
    >
      {{ $t("quickSearch.suggestions") }}
    </h2>
    <NuxtLink
      v-for="suggestion in searchSuggestions"
      :key="suggestion.id"
      :to="getSuggestionLink(suggestion)"
      type="button"
      :data-cy="`${isMobile ? 'mobile' : 'desktop'}-search-suggestion`"
      data-test="suggestion-item"
      class="py-1 flex items-center truncate text-base cursor-pointer hover:font-semibold whitespace-pre"
      @click="onSearchSuggestionClicked(suggestion)"
    >
      <template
        v-for="subString in highlightSuggestion(suggestion.name)"
        :key="subString.value"
      >
        <strong v-if="subString.type === 'bold'">
          {{ subString.value }}
        </strong>
        <template v-else>
          {{ subString.value }}
        </template>
      </template>
    </NuxtLink>
    <button
      v-if="searchTerm"
      class="font-semibold uppercase text-base !p-0"
      :class="{ 'mt-6': searchSuggestionsShown }"
      :data-cy="`${isMobile ? 'mobile' : 'desktop'}-quick-search-show-all`"
      @click="onSearchAllClicked(searchTerm)"
    >
      {{
        $t("quickSearch.showAll", {
          PLACEHOLDER_searchTerm: searchTerm,
        })
      }}
    </button>
  </div>
</template>
